import { useEffect, useRef, useState, useMemo } from "react";
import { allowOrWarn, Util } from '../../util/Util';
import Report from "./Report";
import { useAuth } from "../../hooks/useAuth";
import { useUI } from "../../providers/MoleUIContext";
import { useData } from "../../hooks/useData";
import { toast } from "react-toastify";
import ConfirmDialog from "../../components/ConfirmDialog";
import { periodRequestConfig } from "../../hooks/useDateRange";
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { Box, IconButton, ListItemIcon, MenuItem, Tooltip } from '@mui/material';
import { LocalStorageKeys, NUMBER_FILTER_OPTIONS, TEXT_FILTER_OPTIONS } from "../../util/constants";
import TableView from "../../components/TableView";
import { Delete, DeleteOutline, Refresh, Visibility } from "@mui/icons-material";
import { MRT_ColumnDef } from "material-react-table";
import ExportMenuButton from "../../components/ExportMenuButton";
import { useAppSocket } from "../../providers/AppSocketContext";
import { parseISODay } from "../../util/businessClock";

import { roundQty } from "../../util/quantity";
import { canDrop } from '../../util/access';
const Deposit = () => {
	const [toDelete, setToDelete] = useState<any>(null);
	const [toDeleteBulk, setToDeleteBulk] = useState<any[]>([]);
	const [toView, setToView] = useState<any>(null);

	const { api, session, access } = useAuth();
	const { appSettings } = useAppSocket();
	const { setLoadingMessage, queueReRun, t } = useUI();
	const { data, lastUpdate, loadFromIndexedDB, saveFromServer, dateRange, setDateRange, loading, setLoading } = useData(LocalStorageKeys.DEPOSIT_RECORDS);

	const toastIdRef = useRef<string | number>(-1);
	const tableRef = useRef<any>(null);
	const firstFetchRef = useRef(false);

	useEffect(() => {
		let cancelled = false;
		(async () => {
			const loaded = await loadFromIndexedDB();
			if (cancelled || firstFetchRef.current) return;
			firstFetchRef.current = true;
			if (!loaded) fetchData();
		})();
		return () => {
			cancelled = true;
		};
	}, []);

	const fetchData = async (useLoadModal: boolean = true) => {

		setLoading(true);

		const res = await api.get("deposits", {
			...(periodRequestConfig(dateRange))
		});

		if (res) {
			if (res.status === 200) {
				saveFromServer(res.data, dateRange);
				toast.update(toastIdRef.current, {
					render: t("common:toast.success.refresh"),
					type: "success",
					isLoading: false,
					autoClose: 3000,
				});
			} else if (res.status === 401 || res.status === 422) {
				queueReRun(fetchData);
			} else {
				console.error("Failed to fetch data:", res);
				toast.dismiss(toastIdRef.current);
			}
		} else {
			toast.dismiss(toastIdRef.current);
		}

		setLoading(false);
	};

	const handleDelete = async () => {
		if (toDelete) {
			setLoadingMessage("DELETE");
			const res = await api.delete(`/deposits/${toDelete.batch}`);
			if (res.status === 204) {
				fetchData();
				toastIdRef.current = toast.loading(`${t("common:toast.success.delete")} ${t("common:toast.loading.refresh")}`);
			} else if (res.status === 409) {
				// Not "already deposited": deposit_detail cascades to all four link
				// tables, so a conflict here is a genuine integrity error and the
				// server sends the specific reason.
				toast.error(t("common:toast.error.delete"), { autoClose: 3000 });
			} else if (res.status === 401 || res.status === 422) {
				queueReRun(handleDelete);
			}
			setLoadingMessage(null);
			setToDelete(null);
		}
	};

	const handleBulkDelete = async () => {
		if (toDeleteBulk.length > 0) {
			const payload = { batches: toDeleteBulk };

			setLoadingMessage("DELETE");

			const res = await api.post("/deposits/delete", payload);

			if (res) {
				if (res.status === 204) {
					toastIdRef.current = toast.loading(`${t("common:toast.success.delete")} ${t("common:toast.loading.refresh")}`);
					tableRef.current?.resetRowSelection();
					fetchData();
				} else if (res.status === 409) {
					toast.error(t("common:toast.error.delete"), { autoClose: 3000 });
				} else if (res.status === 401 || res.status === 422) {
					queueReRun(handleBulkDelete);
				}
			}

			setLoadingMessage(null);
			setToDeleteBulk([]);
		}
	};

	// Define table columns
	const columns = useMemo<MRT_ColumnDef<any>[]>(() => [
		{
			header: t("common:tables.depositDate"),
			id: "date",
			accessorFn: (row: any) => parseISODay(row.date),
			Cell: ({ cell }: { cell: any }) => Util.formatDate(cell.getValue(), "medium"),
			filterVariant: 'date-range',
		},
		{
			header: t("common:tables.deposit"),
			accessorKey: "batch",
			Cell: ({ cell }: { cell: any }) => (
				<button
					style={{ background: "none", border: "none", padding: 0, color: "inherit", cursor: "pointer", textDecoration: "underline" }}
					onClick={() => setToView(cell.row.original)}
				>
					{cell.getValue()}
				</button>
			),
			filterFn: 'contains',
			columnFilterModeOptions: TEXT_FILTER_OPTIONS,
		},
		{
			header: t("common:deposits.pendingDetail.income"),
			accessorKey: "income",
			Cell: ({ cell }: { cell: any }) => Util.formatNumber(cell.getValue()),
			filterFn: 'contains',
			columnFilterModeOptions: NUMBER_FILTER_OPTIONS,
		},
		{
			header: t("common:deposits.pendingDetail.expenses"),
			accessorKey: "expenses",
			Cell: ({ cell }: { cell: any }) => Util.formatNumber(cell.getValue()),
			filterFn: 'contains',
			columnFilterModeOptions: NUMBER_FILTER_OPTIONS,
		},
		{
			header: t("common:tables.amount"),
			accessorKey: "amount",
			Cell: ({ cell }: { cell: any }) => Util.formatNumber(cell.getValue()),
			filterFn: 'contains',
			columnFilterModeOptions: NUMBER_FILTER_OPTIONS,
			aggregationFn: 'sum', //show the max age in the group (lots of pre-built aggregationFns to choose from)
			//required to render an aggregated cell
			AggregatedCell: ({ cell, table }) => (
				<>
					{t("common:tableView.sumBy")}{' '}
					{table.getColumn(cell.row.groupingColumnId ?? '').columnDef.header}:{' '}
					<Box
						sx={{ color: 'info.main', display: 'inline', fontWeight: 'bold' }}
					>
						{Util.formatNumber(cell.getValue<number>())}
					</Box>
				</>
			),
			// Add Footer for Grand Total
			Footer: ({ table }) => {
				const [total, setTotal] = useState<number>(0);
				const [isCalculating, setIsCalculating] = useState(false);

				useEffect(() => {
					setIsCalculating(true);
					setTotal(0);

					const rows = table.getFilteredRowModel().rows;
					// run in a micro delay, allowing paint before heavy computation
					const handle = setTimeout(() => {
						let sum = 0;
						for (let i = 0; i < rows.length; i++) {
							const value = Number(rows[i].original.amount);
							if (Number.isFinite(value)) sum += value;
						}
						setTotal(roundQty(sum));
						setIsCalculating(false);
					}, 0); // <— yields to browser first

					return () => clearTimeout(handle);
				}, [table.getFilteredRowModel().rows]);

				return (
					<Box sx={{ fontWeight: 'bold', color: 'success.main' }}>
						{t("common:report.total")}: {Util.formatNumber(total)}
					</Box>
				);
			},
		},
		{
			header: t("common:tables.user"),
			accessorKey: "user",
			filterVariant: 'autocomplete',
			filterFn: 'contains',
			columnFilterModeOptions: TEXT_FILTER_OPTIONS,
		}
	], [t]);

	return (
		<>
			<LocalizationProvider dateAdapter={AdapterDayjs}>
				<TableView
				stateKey={LocalStorageKeys.DEPOSIT_RECORDS}
				posting="deposit"
					dataScope="deposit"
					header={t("common:deposits.headers.records")}
					data={data}
					columns={columns}
					totals={[
						{ key: "income", label: t("common:deposits.pendingDetail.income") },
						{ key: "expenses", label: t("common:deposits.pendingDetail.expenses") },
						{ key: "amount", label: t("common:tables.amount") },
					]}
					lastUpdated={lastUpdate}
					period={dateRange}
					setDateRangeFilter={setDateRange}
					isLoading={loading}

					renderTopToolbarActions={(table) => (
						<Box sx={{ display: 'flex', gap: '0.5rem' }}>
							<Tooltip title={t("common:ui.tooltips.refreshData")}>
								<IconButton onClick={() => fetchData()} color="primary">
									<Refresh />
								</IconButton>
							</Tooltip>
							{table.getIsSomeRowsSelected() && (
								<Tooltip title={t("common:ui.tooltips.deleteSelection")}>
									<IconButton
										onClick={() => {
											if (allowOrWarn(canDrop(access, 'deposit', 'delete_bulk'))) {
												const rowsBatches = [
													...new Set(
														table.getSelectedRowModel().flatRows.map((row: any) => row.original.batch)
													),
												];
												if (rowsBatches.length === 1) {
													setToDelete(table.getSelectedRowModel().flatRows[0].original);
												} else {
													tableRef.current = table;

													setToDeleteBulk(rowsBatches);
												}
											}
										}}
										color="error">
										<Delete />
									</IconButton>
								</Tooltip>
							)}
							<ExportMenuButton
								prefix="deposits"
								data={data}
								columns={columns}
								table={table}
							/>
						</Box>
					)}
					renderRowActionMenuItems={(row, closeMenu) => [
						<MenuItem
							key={0}
							onClick={() => {
								setToView(row.original);
								closeMenu();
							}}
						>
							<ListItemIcon>
								<Visibility />
							</ListItemIcon>
							{t("common:ui.buttons.view")}
						</MenuItem>,
						!toDelete ? (
							<MenuItem
								key={1}
								onClick={() => {
									if (allowOrWarn(canDrop(access, 'deposit'))) {
										setToDelete(row.original);
									}
									closeMenu();
								}}
								sx={{
									color: 'error.main',
								}}
							>
								<ListItemIcon sx={{
									color: 'error.main',
								}}>
									<Delete />
								</ListItemIcon>
								{t("common:ui.buttons.delete")}
							</MenuItem>
						) : null,
					]}
				/>

			</LocalizationProvider>

			{/* Invoice Modal */
				toView &&
				<Report
					isOpen={!!toView}
					toggle={() => setToView(null)}
					bundle={toView}
				/>
			}

			{/* Confirmation Modal: no conditional rendering for perfomance as it is lightweight */
				<ConfirmDialog
					onConfirm={toDelete ? handleDelete : handleBulkDelete}
					onCancel={() => { toDelete ? setToDelete(null) : setToDeleteBulk([]) }}
					isOpen={toDelete || toDeleteBulk.length > 0}
					setIsOpen={() => { }}
					title={<><DeleteOutline sx={{ color: "red" }} /> {t("common:dialog.deleteConfirmation")}</>}
					message={
						toDelete ?
							<>
								{t("common:deposits.confirm.deleteSingleAmount")}&nbsp;
								<b><u>{Util.formatCurrency(toDelete?.amount, appSettings?.BIZ_CURRENCY?.value || "USD")}</u></b>&nbsp;
								{t("common:deposits.confirm.deleteSingleDate")} <b><u>{Util.formatDate(toDelete?.date, "full")}</u></b>&nbsp;
								{t("common:deposits.confirm.deleteSingleUser")} <b><u>{toDelete?.user}</u></b>.
							</>
							:
							<>
								{t("common:deposits.confirm.deleteBulk", { count: Number(toDeleteBulk.length) })}
							</>
					}
					confirmBtnColor="error"
					confirmBtnVariant="contained"
					confirmBtnText={<>{t("common:ui.buttons.delete")}</>}
				/>
			}
		</>
	);
};

export default Deposit;
